import { ContextMenu } from "radix-ui";
import { useRef, useCallback, useState } from "react";
import { useSelector } from "react-redux";
import { selectAccessToken, selectLocation } from "../store/auth";
import * as customerApi from "../api/customer";
import { TRANSACTION_REASON_TO_TEXT } from "../domain/transaction";
import { formatObject } from "../common/formatting";
import toast from "react-hot-toast";
import Modal from "../Modal";
import { useTranslation } from "../i18n";

/**
 * @template T
 * @param {object} props
 * @param {React.ReactNode} children
 * @param {{[key: string]: string}} row
 * @param {() => void} [props.onTransactionCancelled]
 * @returns {React.Component}
 */

export default function HistoryContextMenu({
  children,
  row,
  onTransactionCancelled,
}) {
  const { t, locale } = useTranslation();
  const timeoutRef = useRef(null);
  const accessToken = useSelector(selectAccessToken);
  const managerLocation = useSelector(selectLocation);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [warningMessage, setWarningMessage] = useState("");
  const [cancelComment, setCancelComment] = useState("");

  const showConfirmation = () => {
    if (!row.id) {
      console.error("Transaction ID not found in row:", row);
      toast.error(t("toast.transactionIdNotFound"));
      return;
    }

    // Determine warning message based on transaction reason
    const transactionReason = row.reason;
    if (transactionReason === 2) {
      // PackageBought - online purchase
      setWarningMessage(t("history.cancelWarningPurchase"));
    } else if (transactionReason === 7 || transactionReason === 8) {
      // PackageBoughtWithPos or PackageBoughtWithCash
      setWarningMessage(t("history.cancelWarningPos"));
    } else {
      setWarningMessage("");
    }

    setCancelComment("");
    setShowConfirmModal(true);
  };

  async function confirmCancellation() {
    setShowConfirmModal(false);

    const comment = cancelComment.trim();
    if (!comment) {
      toast.error(t("history.cancelCommentRequired"));
      return;
    }

    try {
      const result = await customerApi.cancelTransaction(
        accessToken,
        row.id,
        comment,
      );

      // Show different messages based on response
      if (result.pending_confirmation) {
        toast.success(
          result.message + ". Данные обновятся после подтверждения.",
          {
            duration: 5000,
          },
        );

        // Set up periodic refresh for pending cancellations
        const refreshInterval = setInterval(async () => {
          if (onTransactionCancelled) {
            await onTransactionCancelled();
          }
        }, 5000); // Check every 5 seconds

        // Clear interval after 3 minutes to prevent infinite polling
        setTimeout(() => clearInterval(refreshInterval), 180000);
      } else {
        toast.success(result.message);
      }

      // Refresh the customer data immediately
      if (onTransactionCancelled) {
        onTransactionCancelled();
      }
    } catch (error) {
      console.error("Failed to cancel transaction:", error);

      // Show specific error message if available
      let errorMessage = t("history.cancelFailed");
      if (error?.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage);
    }
  }

  // Long press handlers for tablet support
  const handleTouchStart = useCallback(() => {
    timeoutRef.current = setTimeout(() => {
      showConfirmation();
    }, 1000);
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  return (
    <ContextMenu.Root>
      <ContextMenu.Trigger
        asChild
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
      >
        {children}
      </ContextMenu.Trigger>
      <ContextMenu.Portal>
        <ContextMenu.Content className="bg-c-gray-500 rounded py-3 px-2">
          <ContextMenu.Item
            onClick={showConfirmation}
            className="py-1 px-2 rounded ring-white bg-c-red-500 cursor-pointer hover:ring-[1.5px]"
          >
            {t("history.cancelTransaction")}
          </ContextMenu.Item>
        </ContextMenu.Content>
      </ContextMenu.Portal>
      <Modal
        title={t("history.cancelConfirm")}
        content={
          <div className="space-y-4">
            {warningMessage && (
              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded">
                <p className="text-gray-900 whitespace-pre-line">
                  {warningMessage}
                </p>
              </div>
            )}

            <div className="space-y-2 rounded-lg bg-c-gray-500 p-3 text-sm">
              <CancelDetailRow
                label={t("history.cancelType")}
                value={formatTransactionType(row.reason, locale)}
              />
              <CancelDetailRow
                label={t("history.location")}
                value={row.location?.name}
              />
              <CancelDetailRow
                label={t("history.cancelMinutes")}
                value={formatMinutes(row.minutesAmount, t)}
              />
              <CancelDetailRow
                label={t("history.amount")}
                value={formatObject(row.priceRub, "priceRub")}
              />
            </div>

            {isLocationMismatch(managerLocation, row.location?.name) && (
              <div className="rounded-lg border border-c-red-500/30 bg-c-red-500/20 p-3 text-sm text-c-red-500">
                {t("history.cancelLocationMismatch", {
                  transactionLocation: row.location?.name,
                  userLocation: managerLocation.name,
                })}
              </div>
            )}

            <p className="text-sm text-c-gray-400">
              {t("history.cancelIrreversible")}
            </p>

            <div>
              <label
                className="block text-sm text-c-gray-400 mb-1"
                htmlFor="cancel-comment"
              >
                {t("history.cancelComment")}
              </label>
              <textarea
                id="cancel-comment"
                value={cancelComment}
                onChange={(e) => setCancelComment(e.target.value)}
                rows={3}
                maxLength={1000}
                placeholder={t("history.cancelCommentPlaceholder")}
                className="flex w-full rounded-lg border border-c-gray-400/30 bg-c-gray-500 px-3 py-2 text-sm text-white placeholder:text-c-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent resize-y"
              />
            </div>
          </div>
        }
        showModal={showConfirmModal}
        setShowModal={setShowConfirmModal}
        onConfirm={confirmCancellation}
        confirmButtonText={t("history.cancelConfirm")}
        disableConfirmBtn={!cancelComment.trim()}
        style="alert"
      />
    </ContextMenu.Root>
  );
}

function CancelDetailRow({ label, value }) {
  if (!value) {
    return null;
  }

  return (
    <div className="flex justify-between gap-4">
      <span className="text-c-gray-400">{label}</span>
      <span className="text-right font-medium text-white">{value}</span>
    </div>
  );
}

function formatTransactionType(reason, locale) {
  const dict =
    TRANSACTION_REASON_TO_TEXT[locale] ?? TRANSACTION_REASON_TO_TEXT["ru"];
  const text = dict[reason];
  if (text == null) {
    console.error("Got unknown transaction reason:", reason);
  }
  return text;
}

function formatMinutes(amount, t) {
  return `${amount > 0 ? "+" : ""}${amount} ${t("common.minutes")}`;
}

function isLocationMismatch(managerLocation, transactionLocation) {
  return (
    managerLocation != null &&
    transactionLocation != null &&
    managerLocation.name !== transactionLocation
  );
}
